import { useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Plus } from "lucide-react";
import type { Opportunity } from "@salesboard/contracts";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "@/components/ui/data-table/DataTable";
import { ActiveFilterChips } from "@/components/ui/data-table/ActiveFilterChips";
import { ListToolbar } from "@/components/ui/data-table/ListToolbar";
import { PaginationControl } from "@/components/ui/data-table/PaginationControl";
import { EmptyState, ErrorState, LoadingState, NoResultState } from "@/components/ui/states";
import { OwnerBadge } from "@/components/ui/OwnerBadge";
import { StageChip } from "@/components/ui/StageChip";
import { useServerTable } from "@/hooks/useServerTable";
import { ApiClientError } from "@/lib/api";
import { formatCurrency, formatDate, formatQuarter } from "@/lib/format";
import { useOpenDetail } from "@/app/useOpenDetail";
import { usePrefetchDetail } from "@/app/usePrefetchDetail";
import { cn } from "@/lib/utils";
import { SummaryStrip } from "@/features/analytics/SummaryStrip";
import { usePipelineScope } from "@/features/board/usePipelineScope";
import { useTvMode } from "@/features/board/useTvMode";
import { YearScope } from "@/features/board/YearScope";
import { useOpportunityFilterDefs } from "./filterDefs";
import { OpportunityFormDialog } from "./OpportunityFormDialog";
import { useOpportunities } from "./hooks";

/**
 * The flat list. When `embedded`, the Pipeline shell owns the page header and the view
 * toggle, so this renders only its own toolbar and table.
 *
 * `stripVisible` is the Pipeline shell's own display preference, passed down rather than read
 * again here — one owner of the state, one button that changes it. On this screen's *own*
 * route there is no such button, so the strip stays: state with no control is exactly what the
 * URL-state rules forbid.
 */
export function OpportunitiesPage({
  embedded = false,
  stripVisible = true,
}: { embedded?: boolean; stripVisible?: boolean } = {}): React.JSX.Element {
  const t = useT();
  const openDetail = useOpenDetail();
  const prefetchDetail = usePrefetchDetail();
  const [createOpen, setCreateOpen] = useState(false);

  // Embedded, the Pipeline shell owns the title — this route has its own.
  useDocumentTitle(embedded ? undefined : t("opportunities.title"));

  // The same definitions the board renders, from the same module — including the client
  // *select* that replaces the raw-UUID text box this screen used to carry.
  const filters = useOpportunityFilterDefs();
  const scope = usePipelineScope();
  // Presented, the toolbar goes and its two facts — the year and whether the list is
  // filtered — become a static caption, exactly as the board's header does.
  const { tv } = useTvMode();

  const table = useServerTable("opportunities", filters, { field: "expectedCloseDate", direction: "asc" });
  // Year is a scope, not a filter: it is not in `filters`, so it never becomes a chip, and
  // it is applied here so the list shows the same year the board does. Until this
  // increment the list ignored it, and toggling the view silently changed the data set.
  //
  // Held until the window resolves rather than sent unscoped — an unscoped list is every
  // year at once, which is a different data set, not a slightly early one.
  const query = useOpportunities(
    { ...table.queryParams, year: scope.year },
    { enabled: scope.year !== undefined },
  );

  // Presented, the table's type follows the same scale the board's cards do — the
  // `board-type-*` hooks, not a parallel set, so one variable in index.css moves both.
  const columns: Column<Opportunity>[] = [
    {
      key: "clientName",
      // The client and opportunity name were always read as a pair — merged into one
      // column, two lines, mirroring the board card's own hierarchy (client primary,
      // name muted beneath it) so the two views read the same way. The column's default
      // click sorts by client; the header's own second, smaller control keeps the
      // opportunity-name sort reachable through the `name` key it already had — one
      // visual column, two of `OPPORTUNITY_SORT_FIELDS`. `sortable` is false here because
      // this header builds its own sort controls rather than DataTable's single-key one.
      header: (
        <span className="inline-flex flex-col items-start gap-0.5">
          <ColumnSortButton label={t("column.client")} field="clientName" sort={table.sort} onToggle={table.toggleSort} />
          <ColumnSortButton
            label={t("column.opportunity")}
            field="name"
            sort={table.sort}
            onToggle={table.toggleSort}
            muted
          />
        </span>
      ),
      sortable: false,
      render: (row) => <ClientOpportunityCell row={row} onOpen={openDetail} tv={tv} />,
    },
    {
      key: "stage",
      header: t("column.stage"),
      sortable: true,
      className: tv ? "board-type-secondary" : undefined,
      // The stage name carries the outcome — "Closed Won" already says it — so the chip's
      // tone is decoration on top of the name, never the only carrier of it. No `(Won)` /
      // `(Lost)` parenthetical: that duplicated what the name already said.
      render: (row) => <StageChip name={row.stageName} closedType={row.status} color={row.stageColor} />,
    },
    {
      key: "nip",
      header: t("column.expectedRevenue"),
      align: "right",
      sortable: true,
      className: tv ? "board-type-secondary" : undefined,
      render: (row) => formatCurrency(row.nip),
    },
    {
      key: "expectedCloseDate",
      header: t("column.expectedClose"),
      sortable: true,
      className: tv ? "board-type-secondary" : undefined,
      // The quarter is a second row beneath the date, not a parenthetical beside it — the
      // detail modal's own bracket notation, in the shape a table column can hold. Quarter
      // and year are derived server-side; shown, never computed here.
      render: (row) => (
        <span className="block">
          <span className="block">{formatDate(row.expectedCloseDate)}</span>
          <span className={cn("text-muted-foreground block", tv ? "board-type-meta" : "text-xs")}>
            {formatQuarter(row.quarter, row.year)}
          </span>
        </span>
      ),
    },
    {
      key: "ownerName",
      header: t("column.owner"),
      sortable: true,
      className: tv ? "board-type-secondary" : undefined,
      // The board's own initials circle, before the name — one ownership rendering across
      // both surfaces. The name stays: a table has the width the card did not.
      render: (row) => (
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <OwnerBadge name={row.ownerName} color={row.ownerColor} />
          <RowLink label={row.ownerName} to={`/user/${row.ownerSlug}`} onOpen={openDetail} />
        </span>
      ),
    },
  ];

  return (
    <>
      {/* Embedded, the Pipeline shell owns the header. On its own route this screen must
          still offer a year selector, or it is scoped to a year with no way to change it. */}
      {/* Standalone, this screen carries its own one control row (2026-08-25): the year
          scope and the creating action beside its search and filters. Embedded in the
          pipeline, the shell renders that row instead — see below. */}
      {!embedded && <h1 className="sr-only">{t("opportunities.title")}</h1>}

      {/* Presented, the toolbar goes — search and the filter panel are exactly the chrome a
          wall display drops — and the caption below says the same two things the board's
          does: which year, and that the numbers are a filtered subset. */}
      {/* Embedded in the pipeline, the shell renders the one control row for both views
          (2026-08-25, specs/pipeline-header.md); standalone at /opportunities this screen is
          its own surface and keeps its toolbar. Presented, the toolbar goes either way —
          search and a filter panel are exactly the chrome a wall display drops. */}
      {!tv && !embedded && (
        <ListToolbar
          table={table}
          defs={filters}
          searchPlaceholder={t("pipeline.searchPlaceholder")}
          actions={
            <>
              <YearScope scope={scope} />
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                {t("pipeline.newOpportunity")}
              </Button>
            </>
          }
        />
      )}

      {tv && (
        <div className="mb-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {scope.year === undefined ? t("pipeline.title") : t("pipeline.titleYear", scope.year)}
          </h1>
          {table.activeFilterCount > 0 && (
            <span className="bg-primary/10 text-primary rounded-full px-2.5 py-0.5 text-sm font-medium">
              {t("board.filteredWith", table.activeFilterLabels.join(" · "))}
            </span>
          )}
        </div>
      )}

      {/* The strip is bound to the pipeline header and the filter set, not to the grid — so
          a list reader gets the same five numbers a board reader does, from the same query.
          Presented, its own `tv` variant reads at distance too — and it renders whatever the
          toggle says, because the button that could hide it is not on the wall to press. */}
      {(tv || stripVisible) && <SummaryStrip filters={table} year={scope.year} tv={tv} />}

      {/* Embedded in the pipeline the shell's toolbar does not draw the chips, so they render
          here — immediately above the table, below the strip, exactly where the board draws
          its own (2026-08-26). Standalone, the toolbar above still owns them. */}
      {!tv && embedded && <ActiveFilterChips filters={table} />}

      {/* The list is held until the year window arrives, so a failure to fetch it would
          otherwise read as loading that never ends. */}
      {scope.yearsError && (
        <ErrorState
          message={
            scope.yearsError instanceof ApiClientError
              ? scope.yearsError.message
              : t("pipeline.yearFailed")
          }
          requestId={scope.yearsError instanceof ApiClientError ? scope.yearsError.requestId : "unknown"}
          onRetry={() => void scope.refetchYears()}
        />
      )}

      {!scope.yearsError && query.isPending && <LoadingState rows={8} label={t("opportunities.loading")} />}

      {query.error && (
        <ErrorState
          message={query.error instanceof ApiClientError ? query.error.message : t("opportunities.loadFailed")}
          requestId={query.error instanceof ApiClientError ? query.error.requestId : "unknown"}
          onRetry={() => void query.refetch()}
        />
      )}

      {query.data && query.data.items.length === 0 && table.activeFilterCount === 0 && (
        <EmptyState
          // The year is named because the list is now scoped to one: without it, a year
          // with no deals reads as "there are no opportunities at all", and the way out —
          // the year selector in the header — would not be obvious.
          title={
            scope.year === undefined
              ? t("opportunities.emptyTitle")
              : t("opportunities.emptyTitleYear", scope.year)
          }
          description={t("opportunities.emptyDescription")}
          action={{ label: t("pipeline.newOpportunity"), onClick: () => setCreateOpen(true) }}
        />
      )}

      {query.data && query.data.items.length === 0 && table.activeFilterCount > 0 && (
        <NoResultState
          activeFilters={table.activeFilterLabels}
          onClear={table.clearFilters}
          scope={scope.year === undefined ? undefined : t("opportunities.noResultScope", scope.year)}
        />
      )}

      {query.data && query.data.items.length > 0 && (
        <>
          <DataTable
            columns={columns}
            rows={query.data.items}
            rowKey={(row) => row.id}
            sort={table.sort}
            onToggleSort={table.toggleSort}
            onRowClick={(row) => openDetail(`/opportunity/${row.slug}`)}
        onRowHover={(row) => prefetchDetail(`/opportunity/${row.slug}`, row.id)}
            caption={t("opportunities.caption")}
          />
          <PaginationControl
            page={query.data.page}
            perPage={query.data.perPage}
            total={query.data.total}
            onPageChange={table.setPage}
            onPerPageChange={table.setPerPage}
          />
        </>
      )}

      {createOpen && (
        <OpportunityFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      )}
    </>
  );
}

/**
 * A name inside a clickable row that opens something *else*.
 *
 * `stopPropagation` is the whole point: without it the row's own handler also fires and the
 * opportunity opens on top of the client the reader actually asked for.
 */
function RowLink({
  label,
  to,
  onOpen,
  className,
}: {
  label: string;
  to: string;
  onOpen: (to: string) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`hover:underline ${className ?? ""}`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(to);
      }}
    >
      {label}
    </button>
  );
}

/**
 * The merged client/opportunity cell: client on top at primary weight, the opportunity's
 * own name beneath it, muted and truncated — the board card's own hierarchy, so a reader
 * moving between the two views finds the same shape twice.
 */
function ClientOpportunityCell({
  row,
  onOpen,
  tv,
}: {
  row: Opportunity;
  onOpen: (to: string) => void;
  tv: boolean;
}) {
  return (
    <span className="block min-w-0">
      <RowLink
        label={row.clientName}
        to={`/client/${row.clientSlug}`}
        onOpen={onOpen}
        className={cn("block truncate font-medium", tv && "board-type-primary")}
      />
      <span className={cn("text-muted-foreground block truncate", tv ? "board-type-secondary" : "text-xs")}>
        {row.name}
      </span>
    </span>
  );
}

/**
 * One of a merged column's two independent sort controls — see the client/opportunity
 * header above. Deliberately not the icon-plus-label button `DataTable` builds for an
 * ordinary sortable column: two of those side by side would nest, and a `<button>` inside
 * a `<button>` is invalid markup. Each control here is its own sibling button, so both
 * `clientName` and `name` — the two `OPPORTUNITY_SORT_FIELDS` this column used to expose
 * as separate columns — stay reachable from the one merged header.
 */
function ColumnSortButton({
  label,
  field,
  sort,
  onToggle,
  muted = false,
}: {
  label: string;
  field: string;
  sort: { field: string; direction: "asc" | "desc" };
  onToggle: (field: string) => void;
  muted?: boolean;
}) {
  const active = sort.field === field;
  const Icon = active ? (sort.direction === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
  return (
    <button
      type="button"
      onClick={() => onToggle(field)}
      className={cn(
        "hover:text-foreground inline-flex items-center gap-1",
        muted && "text-muted-foreground text-xs font-normal",
      )}
    >
      {label}
      <Icon className={cn("h-3 w-3", !active && "opacity-40")} aria-hidden />
    </button>
  );
}
